import { SupportActionKey } from "../utility/supportActions";
import { logger } from "../utility/logger";
import { currentRequestScope, RequestScope } from "./ApiService";
import { sameRequestScope } from "./requestScope";

/**
 * A ONE-SHOT INTENT QUEUE between the support assistant's action chips and the screen that
 * hosts the modal.
 *
 * The chip cannot open the modal itself: the `JobAction` instance that owns it is mounted by
 * `pages/JobPage`, which is a route away. So the chip leaves an intent here and navigates; the
 * page picks it up once its job card has settled.
 *
 * ⚠️ MODULE STATE, NOT A STORE SLICE — AND THAT IS THE POINT.
 * The easy-peasy store is persisted to `sessionStorage` (`store/index.ts`). An intent that
 * survived a reload would open a modal the user never asked for, on a screen they arrived at
 * for some other reason, possibly days later. There is nothing here worth persisting and a
 * great deal worth NOT persisting. This follows the house pattern for runtime-only state —
 * `services/regionRuntime.ts`, `services/supportTenantRuntime.ts`.
 *
 * ⚠️ AND IT CARRIES NO PAYLOAD BEYOND A KEY AND A JOB NUMBER.
 * The intent cannot express quantities, reasons, resources or anything else a modal would
 * submit. It says *which form to open, for which production order*. Everything the form sends
 * is typed by the user, in the form, afterwards.
 *
 * ⚠️ AND IT BELONGS TO ONE ORGANISATION.
 * A CORP support user changes organisation with no reload, and production orders are numbered
 * per organisation, so the same number in the next one is a different order. An intent carries
 * the request scope it was asked in (the Function App and the ambient identity, compared as the
 * access provider compares them), is handed over only in that scope, and is dropped when the
 * organisation changes (`dropJobActionIntent`, called by `applySupportTarget`).
 */

export interface JobActionIntent {
  /** Which modal to open. Resolved through `utility/supportActions`, never called directly. */
  readonly key: SupportActionKey;

  /** The production order the modal is for. The claimant must match it exactly. */
  readonly jobNumber: string;

  /** `Date.now()` when the chip was clicked. Drives the TTL below. */
  readonly requestedAt: number;

  /** The organisation the chip was clicked in. The claimant must be in the same one. */
  readonly scope: RequestScope;
}

export type JobActionListener = (intent: JobActionIntent) => void;

/**
 * How long an unclaimed intent stays live.
 *
 * It exists to cover ONE navigation plus the job card fetch behind it. Thirty seconds is
 * generous for that and far too short to survive a user wandering off — which is the failure
 * this bounds: an intent with no expiry would sit in memory for the whole session and fire the
 * moment the user happened to open that production order for an unrelated reason, with no
 * conversation on screen to explain why a form just appeared.
 */
export const JOB_ACTION_TTL_MS = 30_000;

let pending: JobActionIntent | null = null;
let listeners: JobActionListener[] = [];

/**
 * Record an intent, replacing any earlier one.
 *
 * Replacing rather than queueing is deliberate: there is exactly one user, they clicked one
 * chip most recently, and that is the thing they are waiting for. A queue would open a second
 * modal behind the first.
 */
export function requestJobAction(intent: {
  key: SupportActionKey;
  jobNumber: string;
}): void {
  const jobNumber = (intent?.jobNumber ?? "").trim();

  if (!jobNumber) {
    // Nothing could ever claim it — `claimJobAction` matches on the job number. Dropping it
    // loudly beats leaving a permanently unclaimable intent parked in the module.
    logger.warn("supportActionBus: refused an intent with no production order.", intent);
    return;
  }

  pending = { key: intent.key, jobNumber, requestedAt: Date.now(), scope: currentRequestScope() };

  // Copied before iterating: a listener that unsubscribes itself would otherwise reindex the
  // array mid-loop and skip its neighbour.
  [...listeners].forEach((listener) => {
    try {
      listener(pending as JobActionIntent);
    } catch (error) {
      logger.warn("supportActionBus: a listener threw.", error);
    }
  });
}

/**
 * Take the pending intent for `jobNumber`, if there is one.
 *
 * ⚠️ ONE SHOT. A successful claim CLEARS the intent before the caller has looked at it, which
 * is what makes it safe for the host screen to claim BEFORE it runs its guards: an intent that
 * the guards then refuse cannot come back and fire later, on a re-render, on a SignalR message,
 * or after the user has fixed whatever made the guard fail.
 *
 * Returns `null` when there is nothing pending, when the pending intent belongs to a different
 * production order (the user navigated somewhere else), or when it has expired. A mismatch is
 * NOT a claim: the intent stays put for the screen it was meant for. An intent asked for in
 * another organisation is dropped, like an expired one: it can never be claimed here.
 */
export function claimJobAction(jobNumber: string | null | undefined): JobActionIntent | null {
  if (!pending) return null;

  if (Date.now() - pending.requestedAt > JOB_ACTION_TTL_MS) {
    // Expired intents are dropped on the first look at them, whoever is looking. Leaving a
    // stale one in place would let it expire again for the next caller and the one after.
    pending = null;
    return null;
  }

  if (!sameRequestScope(pending.scope, currentRequestScope())) {
    pending = null;
    return null;
  }

  const wanted = (jobNumber ?? "").trim();
  if (!wanted || wanted !== pending.jobNumber) return null;

  const claimed = pending;
  pending = null;
  return claimed;
}

/**
 * Be told when an intent is recorded.
 *
 * Needed for the case a navigation cannot cover: the user is ALREADY on the production order
 * the chip names, so `navigate` is a no-op, nothing remounts, and no effect's dependencies
 * change. Without this the chip would appear to do nothing at all on exactly the screen where
 * it is most likely to be clicked.
 *
 * Returns the unsubscribe function, so it drops straight out of a `useEffect`.
 */
export function subscribeJobAction(listener: JobActionListener): () => void {
  listeners.push(listener);

  return () => {
    listeners = listeners.filter((l) => l !== listener);
  };
}

/**
 * Drop the pending intent. `applySupportTarget` calls it when the organisation changes, so an
 * intent asked for in one organisation is not claimed after a switch away and back either.
 */
export function dropJobActionIntent(): void {
  pending = null;
}

/** Test seam. Drops the pending intent and every listener. */
export function __resetSupportActionBus(): void {
  pending = null;
  listeners = [];
}
